/**
 * VoiceAccountDetail — shared immersive chrome for the 5 account tabs.
 *
 * Full-immersion replacement for the cramped account-settings page. One slim top
 * bar carries ALL the chrome — back to the app, a tiny account identity, the
 * five-tab pill nav (Profile / Security / Billing / Developers / Troubleshooting)
 * driven by `?tab=`, and a quiet workspace badge. Each tab supplies its body via
 * `children`.
 *
 * Identity comes from the REAL `useAuth()`. Brand law: genie tokens only; Live
 * dot uses `genie-success`; signature gradient for the identity chip.
 *
 * Exports the shared building blocks the 5 tabs reuse: AcctPageHead, AcctCard,
 * AcctField, AcctButton, AcctIcon, ACCT_BRAND.
 */
import type { ReactNode } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  ChevronLeft,
  Code,
  CreditCard,
  ShieldCheck,
  User,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";

/** Brand consts (wireframe purple -> genie-secondary; green -> genie-success). */
export const ACCT_BRAND = {
  blue: "#4E9CFF",
  pink: "#FF4DB8",
  purple: "#FF4DB8",
  amber: "#FFBB33",
  green: "#4E9CFF",
} as const;

/** Named account icons backed by Lucide (inline UI only). */
const ACCT_ICONS: Record<string, LucideIcon> = {
  profile: User,
  user: User,
  security: ShieldCheck,
  shield: ShieldCheck,
  billing: CreditCard,
  card: CreditCard,
  developers: Code,
  code: Code,
  troubleshooting: Wrench,
  wrench: Wrench,
  back: ChevronLeft,
};

export const AcctIcon = ({
  name,
  size = 16,
  className,
}: {
  name: keyof typeof ACCT_ICONS | string;
  size?: number;
  className?: string;
}) => {
  const Icon = ACCT_ICONS[name] ?? User;
  return <Icon style={{ width: size, height: size }} className={className} />;
};

export type AccountTab = "profile" | "security" | "billing" | "developers" | "troubleshooting";

const TABS: { id: AccountTab; label: string; icon: LucideIcon }[] = [
  { id: "profile", label: "Profile", icon: User },
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "developers", label: "Developers", icon: Code },
  { id: "troubleshooting", label: "Troubleshooting", icon: Wrench },
];

const VALID_TABS: AccountTab[] = TABS.map((t) => t.id);

function initialsFrom(name?: string, email?: string): string {
  const base = (name || email || "").trim();
  if (!base) return "HG";
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

interface VoiceAccountDetailProps {
  /** Optional override; otherwise reads `?tab` (default "profile"). */
  active?: AccountTab;
  children: ReactNode;
}

export const VoiceAccountDetail = ({ active, children }: VoiceAccountDetailProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const tabParam = (searchParams.get("tab") as AccountTab | null) ?? "profile";
  const activeTab: AccountTab = active ?? (VALID_TABS.includes(tabParam) ? tabParam : "profile");

  const setTab = (tab: AccountTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next);
  };

  const name = userProfile?.full_name || userProfile?.email || "Your account";
  const initials = initialsFrom(userProfile?.full_name, userProfile?.email);

  return (
    <div className="flex flex-col h-screen w-full bg-genie-background overflow-hidden">
      {/* slim immersive top bar */}
      <div className="h-14 flex-shrink-0 px-4 bg-white border-b border-[#F3F4F6] flex items-center justify-between gap-3.5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1.5 flex-shrink-0 text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to app
          </button>

          <div className="w-px h-[22px] bg-gray-200 flex-shrink-0" />

          {/* tiny account identity */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-[30px] h-[30px] rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-extrabold text-white tracking-wide">{initials}</span>
            </div>
            <span className="text-sm font-bold text-[#222B45] tracking-[-0.2px] truncate max-w-[160px]">
              <CopyableEmail email={name} textClassName="truncate" />
            </span>
          </div>

          <div className="w-px h-[22px] bg-gray-200 flex-shrink-0 hidden md:block" />

          {/* tab nav */}
          <div className="hidden md:flex items-center gap-0.5 p-[3px] rounded-full bg-gray-100 border border-gray-200 flex-shrink-0">
            {TABS.map((tab) => {
              const on = tab.id === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTab(tab.id)}
                  className={`h-[30px] px-2.5 rounded-full flex items-center gap-1.5 transition-all ${
                    on ? "bg-white shadow-sm" : "bg-transparent"
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${on ? "text-genie-primary" : "text-gray-400"}`} />
                  <span
                    className={`text-[12.5px] ${
                      on ? "font-bold text-[#222B45]" : "font-semibold text-gray-500"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* quiet workspace badge */}
        <div className="flex items-center flex-shrink-0">
          <div className="h-[30px] px-3 rounded-full bg-gray-100 border border-gray-200 flex items-center gap-1.5">
            <span className="w-[7px] h-[7px] rounded-full bg-genie-success ring-[3px] ring-genie-success/20" />
            <span className="text-[12.5px] font-bold text-gray-600 hidden sm:inline">Help Genie Group</span>
          </div>
        </div>
      </div>

      {/* mobile tab nav */}
      <div className="md:hidden flex items-center gap-0.5 p-[3px] mx-4 mt-3 rounded-full bg-gray-100 border border-gray-200 overflow-x-auto">
        {TABS.map((tab) => {
          const on = tab.id === activeTab;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`h-[30px] px-3 rounded-full flex items-center gap-1.5 flex-shrink-0 transition-all ${
                on ? "bg-white shadow-sm" : "bg-transparent"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${on ? "text-genie-primary" : "text-gray-400"}`} />
              <span
                className={`text-[12.5px] ${
                  on ? "font-bold text-[#222B45]" : "font-semibold text-gray-500"
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* tab body */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col items-center px-6 pt-7 pb-11">
          <div className="w-full max-w-[1040px]">{children}</div>
        </div>
      </div>
    </div>
  );
};

/* ─────── Shared building blocks reused across the five account tabs ─────── */

/** Page heading row at the top of each tab body. */
export const AcctPageHead = ({
  icon,
  title,
  subtitle,
  tint = ACCT_BRAND.blue,
}: {
  icon: string;
  title: string;
  subtitle: string;
  tint?: string;
}) => {
  return (
    <div className="flex items-center gap-3.5 mb-6">
      <div
        className="w-11 h-11 rounded-[13px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${tint}1a` }}
      >
        <AcctIcon name={icon} size={22} className="text-genie-primary" />
      </div>
      <div className="min-w-0">
        <h1 className="text-2xl font-extrabold text-[#222B45] tracking-[-0.6px]">{title}</h1>
        <p className="text-[13.5px] text-[#64748B] mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
};

/** A standard white card with an optional uppercase section label. */
export const AcctCard = ({
  label,
  children,
  className,
}: {
  label?: string;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={`rounded-[18px] border border-genie-border bg-white shadow-sm px-6 py-5 ${className ?? ""}`}>
      {label && (
        <p className="text-xs font-bold tracking-[0.6px] uppercase text-gray-500 mb-4">{label}</p>
      )}
      {children}
    </div>
  );
};

/** A labelled read-only display field — the immersive equivalent of an input row. */
export const AcctField = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[12.5px] font-semibold text-[#64748B]">{label}</label>
      <div className="min-h-[46px] px-3.5 rounded-xl border border-genie-border bg-genie-background flex items-center">
        <CopyableEmail email={value} className="text-[14.5px] text-[#222B45]" />
      </div>
      {hint && <p className="text-[11.5px] text-gray-500">{hint}</p>}
    </div>
  );
};

/** Pill button — `kind` switches solid-gradient / tinted / ghost. */
export const AcctButton = ({
  label,
  kind = "ghost",
  icon,
  onClick,
}: {
  label: string;
  kind?: "solid" | "tinted" | "ghost";
  icon?: string;
  onClick?: () => void;
}) => {
  const solid = kind === "solid";
  const tinted = kind === "tinted";
  const cls = solid
    ? "text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] border-transparent shadow-lg shadow-genie-primary/30"
    : tinted
      ? "text-genie-primary bg-genie-primary/10 border-transparent"
      : "text-[#64748B] bg-white border-genie-border";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 px-4 rounded-full border inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold transition-all hover:opacity-90 ${cls}`}
    >
      {icon && <AcctIcon name={icon} size={15} className={solid ? "text-white" : "text-genie-primary"} />}
      {label}
    </button>
  );
};

export default VoiceAccountDetail;
